/**
 * Rappels SAV pour le personnel autorisé (droit « SAV du site »). Les rappels sont CALCULÉS par la base (get_sav_reminders), jamais stockés :
 * un rappel disparaît seul dès qu'on agit. Un rappel n'envoie jamais rien au client. Ni texte du client ni contenu d'e-mail dans les données.
 * Réglages des règles : super admin seulement (fonctions get_sav_reminder_rules / set_sav_reminder_rule).
 */
import { supabase } from './supabaseClient';

export type SavReminderRuleKey =
  | 'new_unhandled'
  | 'client_reply_unanswered'
  | 'waiting_client'
  | 'stale'
  | 'part_ordered'
  | 'visit_eve'
  | 'visit_past'
  | 'unprotected_alert';

export interface SavReminder {
  request_id: string;
  reference: string;
  client_name: string;
  store_name: string | null;
  rule_key: SavReminderRuleKey;
  priority: number;
  /** Instant de départ du rappel (création, dernière réponse, dernier e-mail, fin de visite...) */
  since_at: string;
  /** Heures écoulées depuis since_at, dimanches exclus */
  elapsed_hours: number;
  delay_hours: number | null;
  /** Début de la visite (règles de visite seulement) */
  visit_at: string | null;
}

/** Dossier SAV sans traitement établi (fenêtre d'alerte) : ni texte du client ni contenu d'e-mail */
export interface SavAlert {
  request_id: string;
  reference: string;
  client_name: string;
  store_name: string | null;
  /** Heures (dimanche exclu) depuis le début du décompte : création ou fin de la dernière protection */
  elapsed_hours: number;
}

/**
 * Visite SAV planifiée aujourd'hui (jour de Bruxelles) sur un dossier ouvert : de quoi l'afficher et ouvrir le dossier, rien d'autre
 * (ni adresse, ni téléphone, ni texte du client). Ne compte pas dans le nombre de la cloche.
 */
export interface SavVisitToday {
  request_id: string;
  reference: string;
  client_name: string;
  store_name: string | null;
  /** Heure de début, « HH:MM » (Bruxelles) */
  start_time: string;
}

export interface SavOverview {
  reminders: SavReminder[];
  alerts: SavAlert[];
  /** Visites planifiées aujourd'hui, triées par heure de début */
  visitsToday: SavVisitToday[];
  /** Rythme de la fenêtre d'alerte en heures ; null quand l'alerte est désactivée */
  alertRepeatHours: number | null;
}

export interface SavReminderRule {
  rule_key: SavReminderRuleKey;
  enabled: boolean;
  /** Délai en heures (null pour les règles de visite, qui n'ont pas de délai) */
  delay_hours: number | null;
  /** Alerte seulement : durée de protection d'un e-mail du personnel, en heures */
  protect_hours: number | null;
  /** Alerte seulement : rythme de la fenêtre, en heures */
  repeat_hours: number | null;
  updated_at: string;
  updated_by: string | null;
}

export const SAV_REMINDER_RULE_KEYS: SavReminderRuleKey[] = [
  'new_unhandled',
  'client_reply_unanswered',
  'waiting_client',
  'stale',
  'part_ordered',
  'visit_eve',
  'visit_past',
];

/** Règle de l'alerte (fenêtre) : réglée dans une section à part de l'écran « Rappels SAV » */
export const SAV_ALERT_RULE_KEY: SavReminderRuleKey = 'unprotected_alert';

export const SAV_REMINDER_LABELS: Record<
  SavReminderRuleKey,
  { title: string; hint: string; short: string }
> = {
  new_unhandled: {
    title: 'Nouvelle demande non prise en charge',
    short: 'À prendre en charge',
    hint: 'Aucun e-mail envoyé, aucune visite, statut inchangé depuis la création.',
  },
  client_reply_unanswered: {
    title: 'Réponse du client sans réponse',
    short: 'Le client a répondu',
    hint: "Le client a répondu et personne n'a encore agi (e-mail, statut, visite).",
  },
  waiting_client: {
    title: 'On attend le client, relancer ?',
    short: 'Relancer le client ?',
    hint: 'Dernier e-mail envoyé : une question (photos, informations, disponibilités, relance), sans réponse.',
  },
  stale: {
    title: 'Dossier en suspens',
    short: 'En suspens',
    hint: 'Dossier en cours sans aucun mouvement (e-mail, réponse, statut, visite).',
  },
  part_ordered: {
    title: 'Pièce commandée, sans suite',
    short: 'Pièce commandée',
    hint: 'Dernier e-mail envoyé : « pièce commandée », sans nouvelle depuis.',
  },
  visit_eve: {
    title: 'Visite programmée : la veille',
    short: 'Visite demain',
    hint: 'Une visite planifiée a lieu demain.',
  },
  visit_past: {
    title: 'Visite passée sans suite',
    short: 'Visite passée',
    hint: 'Une visite est passée mais est restée « planifiée » (à marquer faite ou annulée).',
  },
  unprotected_alert: {
    title: "Dossier non traité (fenêtre d'alerte)",
    short: 'Non traité',
    hint: 'Dossier sans visite programmée, sans e-mail récent du personnel et sans traitement établi.',
  },
};

/** « 26 h » sous 48 h, sinon « 3 j » */
export const formatElapsed = (hours: number): string => {
  const h = Math.max(0, Math.round(hours));
  return h < 48 ? `${h} h` : `${Math.floor(h / 24)} j`;
};

type ReminderRow = Omit<SavReminder, 'elapsed_hours' | 'priority'> & {
  elapsed_hours: number | string;
  priority: number | string;
};
type AlertRow = Omit<SavAlert, 'elapsed_hours'> & { elapsed_hours: number | string };
interface OverviewRow {
  reminders?: ReminderRow[] | null;
  alerts?: AlertRow[] | null;
  visits_today?: SavVisitToday[] | null;
  alert_repeat_hours?: number | string | null;
}

export const savRemindersService = {
  /**
   * Une seule requête pour la cloche (rappels, du plus urgent au moins urgent) et la fenêtre d'alerte (dossiers non traités) :
   * listes vides sans le droit « SAV du site ».
   */
  async getOverview(): Promise<SavOverview> {
    const { data, error } = await supabase.rpc('get_sav_overview');
    if (error) throw error;
    const o = (data ?? {}) as OverviewRow;
    const repeat = o.alert_repeat_hours == null ? null : Number(o.alert_repeat_hours);
    return {
      reminders: (o.reminders ?? []).map(r => ({
        ...r,
        elapsed_hours: Number(r.elapsed_hours),
        priority: Number(r.priority),
      })),
      alerts: (o.alerts ?? []).map(a => ({ ...a, elapsed_hours: Number(a.elapsed_hours) })),
      visitsToday: o.visits_today ?? [],
      alertRepeatHours: repeat !== null && Number.isFinite(repeat) ? repeat : null,
    };
  },

  /** Règles et leurs délais (super admin seulement). */
  async listRules(): Promise<SavReminderRule[]> {
    const { data, error } = await supabase.rpc('get_sav_reminder_rules');
    if (error) throw error;
    return (data ?? []) as SavReminderRule[];
  },

  /**
   * Active ou désactive une règle et change son délai en heures (null pour les règles de visite). Pour l'alerte seulement :
   * `alert` porte la durée de protection d'un e-mail et le rythme de la fenêtre, en heures. Super admin seulement.
   */
  async setRule(
    ruleKey: SavReminderRuleKey,
    enabled: boolean,
    delayHours: number | null,
    alert?: { protectHours: number; repeatHours: number }
  ): Promise<SavReminderRule> {
    const { data, error } = await supabase.rpc('set_sav_reminder_rule', {
      p_rule_key: ruleKey,
      p_enabled: enabled,
      p_delay_hours: delayHours,
      ...(alert ? { p_protect_hours: alert.protectHours, p_repeat_hours: alert.repeatHours } : {}),
    });
    if (error) throw error;
    const row = (Array.isArray(data) ? data[0] : data) as SavReminderRule | undefined;
    if (!row) throw new Error('Règle non modifiée');
    return row;
  },
};
